import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { switchMap } from 'rxjs';
import { LegalExpDeterminationDialogComponent } from '@app/components/investor-qualification/legal-exp-determination-dialog/legal-exp-determination-dialog.component';
import { AccountService } from '@app/services/account.service';
import { AuthStore } from '@app/stores/auth.store';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { ExpDeterminationDialogComponent } from 'src/app/components/investor-qualification/exp-determination-dialog/exp-determination-dialog.component';
import { ExperiencedCongratDialogComponent } from 'src/app/components/investor-qualification/experienced-congrat-dialog/experienced-congrat-dialog.component';
import { ExperiencedDialogComponent } from 'src/app/components/investor-qualification/experienced-dialog/experienced-dialog.component';
import { InexperiencedDialogComponent } from 'src/app/components/investor-qualification/inexperienced-dialog/inexperienced-dialog.component';
import { InvestorRatingDialogComponent } from 'src/app/components/investor-qualification/investor-rating-dialog/investor-rating-dialog.component';
import { KnowledgeTestFormComponent } from 'src/app/components/investor-qualification/knowledge-test-form/knowledge-test-form.component';
import { WizardStepComponent } from 'src/app/components/wizard/wizard-step/wizard-step.component';
import {
  StepType,
  WizardComponent,
  WizardStep,
} from 'src/app/components/wizard/wizard.component';
import { StatementFormComponent } from '../../components/investor-qualification/statement-form/statement-form.component';
import * as InvestmentQualificationFromHelper from './investor-qualification-wizard-form.helper';
import {
  getInvestmentQualificationWizardSteps,
  InvestorSectionType,
} from './investor-qualification-wizard.helper';

@Component({
  selector: 'app-investor-qualification-wizard',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    WizardComponent,
    WizardStepComponent,
    StatementFormComponent,
    KnowledgeTestFormComponent,
  ],
  templateUrl: './investor-qualification-wizard.component.html',
  styleUrl: './investor-qualification-wizard.component.scss',
})
export class InvestmentQualificationWizardComponent implements OnInit {
  private perviousBtnTranslation = 'general.previous';
  private nextBtnTranslation = 'general.next';
  private finishBtnTranslation = 'registrationWizard.finishRegistration';

  protected stepIndex = signal<number>(1);
  protected prevButtonText = signal<string>(this.perviousBtnTranslation);
  protected nextButtonText = signal<string>(this.nextBtnTranslation);

  protected prevButtonHasChevron = signal<boolean>(true);
  protected nextButtonHasChevron = signal<boolean>(true);

  protected store = inject(AuthStore);

  public items = signal<WizardStep<InvestorSectionType>[]>(
    getInvestmentQualificationWizardSteps(),
  );

  statement: FormGroup<InvestmentQualificationFromHelper.investorStatementFormGroup>;
  knowledgeTest: FormGroup<InvestmentQualificationFromHelper.knowledgeTestFormGroup>;
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private modalService: NgbModal,
    private accountService: AccountService,
  ) {
    this.statement =
      InvestmentQualificationFromHelper.getInvestorStatementFormGroup(fb);
    this.knowledgeTest =
      InvestmentQualificationFromHelper.getKnowledgeTestFormGroup(fb);

    effect(() => {
      if (this.stepIndex() == 1) {
        this.nextButtonHasChevron.set(true);
        this.nextButtonText.set(this.nextBtnTranslation);
      }
      if (this.stepIndex() == 2) {
        this.nextButtonHasChevron.set(false);
        this.nextButtonText.set(this.finishBtnTranslation);
      }
    });
  }

  ngOnInit(): void {
    //TODO: check if statements are made
    this.openInvestorRatingDialog();
  }

  private setActive(): void {
    this.items.update(() =>
      this.items().map((item, itemIndex) => ({
        ...item,
        isActive: itemIndex + 1 === this.stepIndex(),
      })),
    );
  }

  private setStepToDone(stepIndex: number): void {
    this.items.update(() =>
      this.items().map((item, itemIndex) => ({
        ...item,
        stepType: itemIndex + 1 === stepIndex ? StepType.done : item.stepType,
      })),
    );
  }

  protected onStepSelection(index: number): void {
    this.stepIndex.set(index + 1);
  }

  protected onPreviousButtonClick(): void {
    if (this.stepIndex() === 1) {
      return;
    }
    this.stepIndex.set(this.stepIndex() - 1);
    this.setActive();
  }

  private handleNextOnFirstStep(): void {
    this.statement.markAllAsTouched();
    if (!this.statement.valid) {
      return;
    }
    this.store
      .setInvestorStatement({
        notQualifiedQ1ownsTransferableSecurities:
          this.statement.value.q1OwnsTransferableSecurities,
        notQualifiedQ2ownsCrowdfundingAssetsOrLoans:
          this.statement.value.q2OwnsCrowdfundingAssetsOrLoans,
        notQualifiedQ3hasTransferableSecurities:
          this.statement.value.q3HasCrowdfundingInvestments,
        notQualifiedQ4hasCrowdfundingInvestments:
          this.statement.value.q4OwnsTransferableSecurities,
        myStatementsAboveAreAccurate:
          this.statement.value.myStatementsAboveAreAccurate,
      })
      .subscribe(() => {
        this.setStepToDone(1);
        this.stepIndex.set(2);
        this.setActive();
      });
  }

  private handleNextOnSecondStep(): void {
    this.statement.markAllAsTouched();
    if (!this.statement.valid) {
      return;
    }
    this.knowledgeTest.markAllAsTouched();
    if (!this.knowledgeTest.valid) {
      return;
    }

    this.accountService
      .acceptKnowledgeTest({
        notQualifiedUseHasUnderstoodLossSimulation:
          this.knowledgeTest.value.understoodTheSimulation ?? false,
        notQualifiedUserIncomesConfirmAccuracy:
          this.knowledgeTest.value.validStatements ?? false,
        notQualifiedUserReceivedLossSimulationResult:
          this.knowledgeTest.value.resultReceived ?? false,
      })
      .subscribe(() => {
        this.router.navigateByUrl('/profile');
      });
  }

  protected onNextButtonClick() {
    switch (this.stepIndex()) {
      case 1:
        this.handleNextOnFirstStep();
        break;
      case 2:
        this.handleNextOnSecondStep();
        break;
    }
  }

  private openExperiencedUserCongratDialog(): void {
    const experiencedUserCongratDialogRef = this.modalService.open(
      ExperiencedCongratDialogComponent,
      { centered: true },
    );

    experiencedUserCongratDialogRef.result.then(() =>
      this.router.navigateByUrl('/profile'),
    );
  }

  private openLegalExperiencedUserCongratDialog(): void {
    const experiencedUserCongratDialogRef = this.modalService.open(
      ExperiencedCongratDialogComponent,
      { centered: true },
    );

    experiencedUserCongratDialogRef.result.then(() =>
      this.router.navigate(['/profile/legal']),
    );
  }

  private openUserIsExperiencedDialog(): void {
    const userIsExperiencedDialogRef = this.modalService.open(
      ExperiencedDialogComponent,
      { centered: true },
    );
    userIsExperiencedDialogRef.result.then((accept: boolean) => {
      this.store
        .acceptExperiencedQualification({
          acceptExperiencedQualification: accept,
        })
        .subscribe((response) => {
          console.log(response);
          if (accept) {
            this.openExperiencedUserCongratDialog();
          }
        });
    });
  }

  private openLegalUserIsExperiencedDialog(): void {
    const userIsExperiencedDialogRef = this.modalService.open(
      ExperiencedDialogComponent,
      { centered: true },
    );
    userIsExperiencedDialogRef.result.then((accept: boolean) => {
      this.store
        .acceptExperiencedQualification({
          acceptExperiencedQualification: accept,
        })
        .pipe(switchMap(() => this.store.refreshLoggedInCompany()))
        .subscribe(() => {
          if (accept) {
            this.openLegalExperiencedUserCongratDialog();
          }
        });
    });
  }

  private openUserInexperiencedDialog(): void {
    const userIsExperiencedDialogRef = this.modalService.open(
      InexperiencedDialogComponent,
      { centered: true },
    );
    userIsExperiencedDialogRef.result.then((startTest: boolean) => {
      if (startTest) {
        this.store.setStartOfKnowledgeTest().subscribe();
      }
    });
  }

  private openExpDeterminationDialog(): void {
    const expDeterminationDialogRef = this.modalService.open(
      ExpDeterminationDialogComponent,
      { centered: true },
    );

    expDeterminationDialogRef.result.then(
      (experienced: {
        minGrossIncome: boolean;
        atLeastOneYerExp: boolean;
        experienced: boolean;
        validStatements: boolean;
      }) => {
        this.store
          .setInvestorQualification({
            question1: experienced.minGrossIncome,
            question2: experienced.atLeastOneYerExp,
            question3: experienced.experienced,
            myStatementsAboveAreAccurate: experienced.validStatements,
          })
          .subscribe((response) => {
            if (response?.investorQualification == 1) {
              this.openUserIsExperiencedDialog();
            } else if (experienced.validStatements) {
              this.openUserInexperiencedDialog();
            }
          });
      },
    );
  }

  private openLegalExpDeterminationDialog(): void {
    const expDeterminationDialogRef = this.modalService.open(
      LegalExpDeterminationDialogComponent,
      { centered: true },
    );

    expDeterminationDialogRef.result.then(
      (experienced: {
        sixtyKor100kYearlyPortfolio: boolean;
        yearinLeadLeadRoleWhere2mNetIncomeOrProf: boolean;
        hasSignificantMarketActivityFor4QuarterTenTransaction: boolean;
        validStatements: boolean;
      }) => {
        const legalEntityId = this.store.loggedInCompany()?.id;

        legalEntityId &&
          this.store
            .setInvestorQualificationForLegalEntity(legalEntityId, {
              question1: experienced.sixtyKor100kYearlyPortfolio,
              question2: experienced.yearinLeadLeadRoleWhere2mNetIncomeOrProf,
              question3:
                experienced.hasSignificantMarketActivityFor4QuarterTenTransaction,
              myStatementsAboveAreAccurate: experienced.validStatements,
            })
            .subscribe((response) => {
              if (response?.investorQualification == 1) {
                this.openLegalUserIsExperiencedDialog();
              } else if (experienced.validStatements) {
                this.openUserInexperiencedDialog();
              } else {
                this.router.navigate(['/profile/legal']);
              }
            });
      },
    );
  }

  private openInvestorRatingDialog(): void {
    const investorRatingDialogRef = this.modalService.open(
      InvestorRatingDialogComponent,
      {
        centered: true,
      },
    );

    investorRatingDialogRef.result.then((result: boolean) => {
      if (result) {
        console.log(JSON.stringify(this.store.loggedInCompany()));
        if (this.store.loggedInCompany() != undefined) {
          this.openLegalExpDeterminationDialog();
        } else {
          this.openExpDeterminationDialog();
        }
      }
    });
  }
}
